import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { auth } from "@/api/endpoints";
import { Spinner } from "@/components/shared/Spinner";

/** Bootstraps auth state on first render: tries /me; if 403 attempts /refresh
 * (the apiFetch client handles retry transparently). */
export function RequireAuth({ children, role }: { children: React.ReactNode; role?: "admin" }) {
  const { user, setAuth } = useAuthStore();
  const [pending, setPending] = useState(user === null);
  const location = useLocation();

  useEffect(() => {
    if (user) {
      setPending(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const me = await auth.me();
        if (!cancelled) setAuth(me, useAuthStore.getState().accessToken);
      } catch {
        // not authenticated — login redirect below
      } finally {
        if (!cancelled) setPending(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, setAuth]);

  if (pending) {
    return (
      <div className="grid place-items-center h-full">
        <Spinner />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  if (role && user.role !== role) {
    return <Navigate to="/inventory" replace />;
  }
  return <>{children}</>;
}
